<template>
  <div id="root" :class="wrapperClasses">
    <header class="top-nav">
      <div class="inner branding">
        <h1 class="site-title selectable" @click="showHome">{{main.title}}</h1>
        <h3 class="subtitle" @click="showHome">{{main.subtitle}}</h3>
      </div>
      <nav class="inner nav-container">
        <div class="trigger-refresh" @click="refresh">
          <b-icon icon="refresh" size="is-large" />
        </div>
        <ul class="menu">
          <li v-for="item in mainMenuItems" :key="item.to" :class="linkItemClass(item)">
            <router-link :to="item.to">{{item.title}}</router-link>
            <ul class="sub-menu" v-if="item.hasChildren && isAdmin">
              <li v-for="sub in item.children" :key="sub.to" class="sub-item">
                <router-link :to="sub.to">{{sub.title}}</router-link>
              </li>
            </ul>
          </li>
        </ul>
        <b-tooltip v-if="loggedIn" class="logout" @click.native="logout" :label="loginInfoTitle" position="is-bottom" multilined>
          <p class="user-info" >{{loginName}}</p>
          <b-icon icon="logout" title="Log out" />
        </b-tooltip>
      </nav>
    </header>
    <main class="main">
      <div class="inner">
        <keep-alive>
          <router-view />
        </keep-alive>
      </div>
    </main>
    <page-footer :footer="footer"></page-footer>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import PageFooter from "./components/PageFooter.vue";
import { bus } from "./main";
import config from "./config/config";

export default {
  name: "app",
  components: {
    PageFooter
  },
  created() {
    this.setUser();
    this.getDataSets();
    window.addEventListener("keydown", this.handleKeyDown);
  },
  computed: {
    ...mapGetters(["main", "mainMenu"]),
    role() {
      return this.user.role;
    },
    isAdmin() {
      return this.role === "admin";
    },
    loggedIn() {
      return this.user.valid && this.user.role !== "none";
    },
    mainMenuItems() {
      return this.mainMenu.filter(this.mayAccessMenuItem);
    },
    wrapperClasses() {
      let cls = ["wrapper"];
      const parts = this.$route.path.replace(/(^\/|\/$)/g, "").split("/");
      let section = "home";
      let sub = "";
      if (parts.length > 0) {
        if (parts[0].length > 1) {
          section = parts.shift();
        }
        if (parts.length > 0) {
          if (parts[0].length > 1) {
            sub = parts.join("--");
          }
        }
      }
      const hasSub = sub.length > 1;
      cls.push("section-" + section);
      if (hasSub) {
        cls.push("sub-" + sub);
      }
      switch (section) {
        case "home":
          cls.push("show-main");
          break;
        default:
          cls.push("show-detail");
          break;
      }
      if (this.showMenu) {
        cls.push("show-menu");
      }
      return cls;
    },
    loginName() {
      return [this.user.firstName, this.user.lastName].join(" ");
    },
    loginInfoTitle() {
      const roleName = this.user.role.replace(/[_-]+/g, ' ');
      return `Logged in as ${this.loginName} (${roleName}). Click to log out!`;
    }
  },
  data() {
    return {
      showMenu: false,
      pagesLoaded: false,
      user: {
        _id: "",
        valid: false,
        role: "none"
      },
      footer: config.footer
    };
  },
  watch: {
    user(val, prevVal) {
      if (val.valid && val.valid !== prevVal.valid) {
        this.user = val;
      }
    }
  },
  methods: {
    ...mapActions(["getDataSets", "fetchUser"]),
    mayAccessMenuItem(item) {
      let mayAccess = true;
      if (item.access) {
        switch (item.access) {
          case "admin":
            mayAccess = this.role === "admin";
            break;
          case "selector":
            mayAccess = ["admin","selector","remote_selector"].includes(this.role);
            break;
        }
      }
      return mayAccess;
    },
    showHome() {
      if (this.$route.path !== "/") {
        this.$router.push("/");
      }
    },
    toggleMenu() {
      this.showMenu = !this.showMenu;
    },
    handleKeyDown(e) {
      switch (e.which) {
        case 37:
          bus.$emit("prev", true);
          break;
        case 39:
          bus.$emit("next", true);
          break;
        case 27:
          bus.$emit("escape", true);
          break;
      }
    },
    setUser() {
      this.fetchUser().then(u => {
        if (u instanceof Object) {
          this.user = u;
        }
      });
    },
    logout() {
      this.user = {
        _id: "",
        valid: false,
        role: "none"
      };
      this.$ls.set("user", { valid: false });
      const { path } = this.$route;
      if (path !== "/" && path.length > 2) {
        this.$router.push("/");
      }
    },
    linkItemClass(item) {
      let cls = ["menu-item"];
      const { path } = this.$route;
      const hasChildMatch = link =>
        link.hasChildren
          ? link.children.some(sub => path.indexOf(sub.to) === 0)
          : false;
      if (path.indexOf(item.to) === 0 || hasChildMatch(item)) {
        cls.push("active-trail");
      }
      return cls;
    },
    refresh() {
      bus.$emit("refresh", true);
    }
  }
};
</script>
<style lang="scss">
@import "./styles/app.scss";
</style>
